import React from 'react';
import { Alert, Button, Stack } from '@mui/material';
import { useLocation } from 'react-router-dom';

/**
 * Catches render errors from the routed page, and surfaces 5xx API failures.
 *
 * This has to be a class. React only routes render errors to a component that
 * implements getDerivedStateFromError/componentDidCatch, and this file was a
 * function component with neither -- so despite its name, nothing in the app
 * caught render errors at all, and there was no other boundary anywhere.
 *
 * What that cost: one `props.filter` TypeError on /mlb/player-props propagated
 * out of hydrateRoot, React unmounted the whole tree leaving an empty
 * <div id="root">, and the resulting uncaught exception became a react-snap
 * pageerror -- which sets its shuttingDown flag and abandons every route still
 * queued. Ten unrelated pages lost their prerendered HTML, and in production
 * every visitor to such a page got a blank white screen with no fallback.
 *
 * The fallback replaces the whole page, navigation included. Each page renders
 * its own `layouts/Main` (Topbar, Footer) from inside <Routes>, so all of it
 * sits under this boundary and goes away together -- which is why the fallback
 * has to carry its own way out.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.handleRejection = this.handleRejection.bind(this);
    this.reset = this.reset.bind(this);
  }

  // A boolean, not the thrown value. `throw null` / `throw ''` / `throw 0` are
  // rare but real (minified vendor code, re-thrown rejections), and storing the
  // value meant a falsy throw left the boundary believing nothing had happened.
  // It would then re-render the throwing subtree, catch again, and loop until
  // React gave up and unmounted -- restoring the empty #root this exists to stop.
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Render error caught by ErrorBoundary:', error, info && info.componentStack);
  }

  componentDidMount() {
    window.addEventListener('unhandledrejection', this.handleRejection);
  }

  // Clear on navigation. Without this, one throw showed the fallback over every
  // subsequent route for the rest of the session.
  //
  // Done here rather than by keying the component on the location: a key would
  // remount the entire <Routes/> subtree on every navigation, error or not, to
  // clear a boolean that is false almost always. That costs nothing today only
  // because no layout route is shared between pages -- add one and every click
  // would tear down and rebuild the nav.
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.locationKey !== this.props.locationKey) {
      this.reset();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleRejection);
  }

  handleRejection(event) {
    const reason = event.reason;

    // Only handle actual HTTP API errors (from our callApi function).
    // Ignore Auth0 SDK errors, network errors, and other non-API rejections.
    if (reason && typeof reason.status === 'number') {
      // Prevent default browser logging for handled errors
      event.preventDefault();
      console.error('Unhandled API error:', reason.status, reason.message);
      // Don't navigate or set error state — let UserContext handle auth flows.
      // Only show UI error for unexpected server errors (5xx).
      if (reason.status >= 500) {
        this.setState({ hasError: true });
      }
    }
    // All other unhandled rejections: let them pass through to console
    // without disrupting the UI or navigation.
  }

  reset() {
    this.setState({ hasError: false });
  }

  render() {
    if (this.state.hasError) {
      return (
        // The data attribute is load-bearing, not decoration:
        // scripts/check-prerender-coverage.js fails the build when it finds this
        // marker in a prerendered file. Without it the boundary would turn a
        // broken page from an empty #root -- which the gate catches -- into a
        // plausible-looking body that passes, and quietly ship this message to
        // crawlers as the page's content.
        <Alert
          data-error-boundary="true"
          severity="error"
          sx={{ m: 3 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={this.reset}>
                RETRY
              </Button>
              {/*
                * The fallback took the nav with it, so it has to offer a way out.
                *
                * A real <a href>, not a RouterLink: when the page that threw IS
                * "/", a client-side push to "/" changes no location and resets
                * nothing, leaving the user on the fallback with both buttons
                * inert. A document load always recovers.
                */}
              <Button color="inherit" size="small" href="/">
                HOME
              </Button>
            </Stack>
          }
        >
          Something went wrong. Please try again.
        </Alert>
      );
    }

    return this.props.children;
  }
}

/**
 * Feeds the current location in so the class can clear itself on navigation.
 *
 * pathname, search AND hash: /pick-history?page=99999 throwing must be
 * recoverable by going to ?page=1, which is not a pathname change, and the same
 * trap applies to a fragment.
 */
const ErrorBoundaryWithReset = ({ children }) => {
  const { pathname, search, hash } = useLocation();
  return <ErrorBoundary locationKey={`${pathname}${search}${hash}`}>{children}</ErrorBoundary>;
};

export default ErrorBoundaryWithReset;
// Named so the class can be mounted in isolation by a test; nothing else uses it.
export { ErrorBoundary };
